/**
 * QuickBooks Online Integration Setup
 *
 * Displayed in the Integrations tab. Handles:
 * - Connect/disconnect OAuth flow
 * - Connection status display
 * - OAuth callback processing (reads code/realmId from URL params)
 */
import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  CheckCircle2,
  XCircle,
  ExternalLink,
  Loader2,
  Unplug,
} from 'lucide-react';
import {
  useQuickBooksStatus,
  useQuickBooksAuthorize,
  useQuickBooksCallback,
  useQuickBooksDisconnect,
} from '@/hooks/useQuickBooks';

export function QuickBooksSetup() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: status, isLoading } = useQuickBooksStatus();
  const authorizeMutation = useQuickBooksAuthorize();
  const callbackMutation = useQuickBooksCallback();
  const disconnectMutation = useQuickBooksDisconnect();
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);

  // Handle OAuth callback (URL has ?qbo_callback=true&code=...&realmId=...&state=...)
  useEffect(() => {
    const isCallback = searchParams.get('qbo_callback') === 'true';
    const code = searchParams.get('code');
    const realmId = searchParams.get('realmId');
    const state = searchParams.get('state');

    if (isCallback && code && realmId && state) {
      // Clear callback params from URL
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('qbo_callback');
      newParams.delete('code');
      newParams.delete('realmId');
      newParams.delete('state');
      setSearchParams(newParams, { replace: true });

      // Exchange code for tokens
      callbackMutation.mutate({ code, state, realm_id: realmId });
    }
  }, []); // Run once on mount

  const handleConnect = async () => {
    const result = await authorizeMutation.mutateAsync();
    if (result.auth_url) {
      // Store state in sessionStorage for verification on return
      sessionStorage.setItem('qbo_oauth_state', result.state);
      // Redirect to Intuit OAuth
      window.location.href = result.auth_url;
    }
  };

  const handleDisconnect = () => {
    disconnectMutation.mutate();
    setShowDisconnectConfirm(false);
  };

  if (isLoading) {
    return (
      <div className="border border-gray-200 rounded-lg p-6">
        <div className="flex items-center gap-2 text-gray-500">
          <Loader2 className="w-4 h-4 animate-spin" />
          Loading QuickBooks status...
        </div>
      </div>
    );
  }

  const isConnected = status?.is_connected;

  return (
    <div className="border border-gray-200 rounded-lg p-6 space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" fill="#2CA01C" />
              <path d="M7 12a3 3 0 0 1 3-3h1v6h-1a3 3 0 0 1-3-3zm4-3h1a3 3 0 0 1 0 6h-1V9z" fill="white" />
            </svg>
            QuickBooks Online
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            Push invoices to QuickBooks to eliminate double-entry.
            Organizations sync as Customers automatically.
          </p>
        </div>

        {isConnected && (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <CheckCircle2 className="w-3 h-3 mr-1" />
            Connected
          </span>
        )}
      </div>

      {/* Callback processing state */}
      {callbackMutation.isPending && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center gap-3">
          <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
          <span className="text-sm text-blue-800">Connecting to QuickBooks...</span>
        </div>
      )}

      {callbackMutation.isError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <XCircle className="w-5 h-5 text-red-600" />
          <span className="text-sm text-red-800">
            {(callbackMutation.error as any)?.message || 'Failed to connect QuickBooks'}
          </span>
        </div>
      )}

      {/* Connected state */}
      {isConnected && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-3">
          <div className="grid grid-cols-2 gap-4 text-sm">
            {status?.company_name && (
              <div>
                <span className="text-gray-500">Company:</span>{' '}
                <span className="font-medium text-gray-900">{status.company_name}</span>
              </div>
            )}
            {status?.realm_id && (
              <div>
                <span className="text-gray-500">Company ID:</span>{' '}
                <span className="font-mono text-gray-700">{status.realm_id}</span>
              </div>
            )}
            {status?.connected_by && (
              <div>
                <span className="text-gray-500">Connected by:</span>{' '}
                <span className="text-gray-900">{status.connected_by}</span>
              </div>
            )}
            {status?.connected_at && (
              <div>
                <span className="text-gray-500">Connected:</span>{' '}
                <span className="text-gray-900">
                  {new Date(status.connected_at).toLocaleDateString()}
                </span>
              </div>
            )}
          </div>

          {/* Disconnect */}
          {showDisconnectConfirm ? (
            <div className="flex items-center gap-3 pt-2 border-t border-green-200">
              <span className="text-sm text-gray-700">
                Disconnect QuickBooks? Sync history will be preserved.
              </span>
              <button
                onClick={handleDisconnect}
                disabled={disconnectMutation.isPending}
                className="px-3 py-1.5 text-sm font-medium text-red-700 bg-red-100 rounded hover:bg-red-200 disabled:opacity-50"
              >
                {disconnectMutation.isPending ? 'Disconnecting...' : 'Yes, disconnect'}
              </button>
              <button
                onClick={() => setShowDisconnectConfirm(false)}
                className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900"
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              onClick={() => setShowDisconnectConfirm(true)}
              className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-red-600 transition-colors pt-2 border-t border-green-200"
            >
              <Unplug className="w-3.5 h-3.5" />
              Disconnect
            </button>
          )}
        </div>
      )}

      {/* Not connected state */}
      {!isConnected && !callbackMutation.isPending && (
        <div className="space-y-4">
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm text-gray-700 space-y-2">
            <p className="font-medium">How it works:</p>
            <ol className="list-decimal list-inside space-y-1 text-gray-600">
              <li>Click "Connect to QuickBooks" below</li>
              <li>Sign in to your QuickBooks Online account</li>
              <li>Authorize access for Keystone</li>
              <li>You'll be redirected back here automatically</li>
            </ol>
            <p className="text-gray-500 pt-1">
              Once connected, you can push individual invoices to QBO with one click.
              Organizations are synced as QBO Customers automatically.
            </p>
          </div>

          <button
            onClick={handleConnect}
            disabled={authorizeMutation.isPending}
            className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {authorizeMutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Preparing...
              </>
            ) : (
              <>
                <ExternalLink className="w-4 h-4" />
                Connect to QuickBooks
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
}
